import { Inject, Injectable } from "@angular/core";
import { FiscalDeviceRepository, ProductRepository, SaleAdvanceAdjustmentRepository, SaleAdvanceRepository, SaleDocumentRepository, SUZ_SERVICE } from "@skylab-angular-shared/business";
import { ApplicationInsightsService } from "@skylab-angular-shared/config";
import { CommandBus, EventBus, L10n } from "@skylab-angular-shared/core";
import { FiscalDeviceRegistryCreateEventCommand, FiscalDeviceRegistryCreateEventData, FiscalDeviceRegistryUpdateEventCommand, FiscalDeviceRegistryUpdateEventData, FiscalDeviceRepositoryBase, isKsefDocumentSendPending, isKsefDocumentSendWithSuccess, SaleAdvanceAdjustmentRepositoryBase, SaleAdvanceRepositoryBase, SaleDocumentRepositoryBase, SaleUpdateFiscalizationStatusCommand, SuzInternalStatus, SuzService } from "@skylab-shared/business";
import { ConsoleLogger, EventBusBase, L10nBase } from "@skylab-shared/core";
import { FiscalDeviceEventKind, FiscalDeviceEventStatus, FiscalizationState, SaleAdvanceDocumentModel, SaleDocumentKind, SaleDocumentModel } from "@skylab-shared/dataModel";
import { ToastDelay } from "@skylab-shared/ui";
import { FiscalToastService } from "./fiscal-toast.service";

@Injectable({providedIn: 'root'})
export class FiscalPrintingActionsService {
	private logger = new ConsoleLogger(this.constructor.name);

	public constructor(
		@Inject(SUZ_SERVICE) private readonly suzService: SuzService,
		@Inject(L10n) private readonly l10n: L10nBase,
		private readonly fiscalToastService: FiscalToastService,
		private readonly commandBus: CommandBus,
		private readonly insights: ApplicationInsightsService,
		@Inject(EventBus) private readonly eventBus: EventBusBase,
		private readonly productRepository: ProductRepository,
		@Inject(FiscalDeviceRepository) private readonly fiscalDeviceRepository: FiscalDeviceRepositoryBase,
		@Inject(SaleDocumentRepository) private readonly saleDocumentRepository: SaleDocumentRepositoryBase,
		@Inject(SaleAdvanceRepository) private readonly advanceRepository: SaleAdvanceRepositoryBase,
		@Inject(SaleAdvanceAdjustmentRepository) private readonly advanceAdjustmentRepository: SaleAdvanceAdjustmentRepositoryBase,
	) { }

	public async printDailyReport(printer: {name: string, id: number}): Promise<void> {
		const fiscalRegistryUpdate = await this.setFiscalRegistryInitEvent({
			deviceId: printer.id,
			data: {
				description: this.l10n._T('scenes.shared.toast.fiscalization.daily-report.events.pending'),
				kind: FiscalDeviceEventKind.DailyReportPrinting,
				status: FiscalDeviceEventStatus.Info
			}
		});
		const response = await this.suzService.printDailyReportAsync(printer);
		if (response.type === SuzInternalStatus.Success) {
			const text = this.l10n._T('scenes.shared.toast.fiscalization.daily-report.success');
			fiscalRegistryUpdate(text, FiscalDeviceEventStatus.OK);
			this.fiscalToastService.showSuccess(text);
		} else {
			const text = this.l10n._T('scenes.shared.toast.fiscalization.daily-report.error');
			fiscalRegistryUpdate(this.prepareTextForRegistryItem(text, response.suzMessages), FiscalDeviceEventStatus.Error);
			this.fiscalToastService.showError(text, response.suzMessages);
		}
	}

	private getDocumentFiscalNotAvailableMessage(documentModel: SaleDocumentModel | SaleAdvanceDocumentModel): string | undefined {
		if (isKsefDocumentSendWithSuccess(documentModel) && !documentModel.invoiceToReceipt) {
			return this.l10n._T('scenes.shared.toast.fiscalization.ksef-document-without-fp');
		} else if (isKsefDocumentSendPending(documentModel)) {
			return this.l10n._T('scenes.sale-document.form.toasts.document-during-ksef-processing');
		}
		return undefined;
	}

	public async printDocument(entity: {id: number, kind: SaleDocumentKind}, selectedPrinter: {id: number, name: string}): Promise<void> {
		let fiscalRegistryUpdate;
		let refreshedDocumentVersion;

		try {
			const repo = this.getRepoByKind(entity.kind);
			const documentModel = await repo.findAsync(entity.id);

			const documentFiscalNotAvailableMessage = this.getDocumentFiscalNotAvailableMessage(documentModel);
			if (documentFiscalNotAvailableMessage) {
				this.fiscalToastService.showError(documentFiscalNotAvailableMessage, [], ToastDelay.Regular);
				return;
			}

			this.fiscalizationStarted(entity.id);
			this.addBeforeUnloadEventListener();

			fiscalRegistryUpdate = await this.setFiscalRegistryInitEvent({
				deviceId: selectedPrinter.id,
				data: {
					description: this.l10n._T('scenes.shared.toast.fiscalization.print-document.events.pending'),
					documentId: entity.id,
					kind: FiscalDeviceEventKind.DocumentFiscalization,
					status: FiscalDeviceEventStatus.Info
				}
			});

			const productsNames: { id: number; name: string }[] = [];
			const itemsIds = [...new Set(documentModel.items.asArray().filter(item => item.productId && item.productId > 0).map(elem => elem.productId))] as number[];

			if (itemsIds.length) {
				const products = await Promise.all(itemsIds.map(async id => {
					return await this.productRepository.findAsync(id);
				}));
				productsNames.push(...products.map(p => ({ id: p.id, name: p.fiscalName ? p.fiscalName : p.name })));
			}
			const printer = await this.fiscalDeviceRepository.findAsync(selectedPrinter.id);

			const response = await this.suzService.printReceiptAsync(documentModel, productsNames, printer);

			if (response.type === SuzInternalStatus.Success && response.data) {
				refreshedDocumentVersion = await this.changeFiscalizationStateOnDocumentAsync(entity.id, response.data);
				if (refreshedDocumentVersion) {
					const text = this.l10n._T('scenes.shared.toast.fiscalization.print-document.success');
					this.fiscalToastService.showSuccess(text);
					fiscalRegistryUpdate(text, FiscalDeviceEventStatus.OK);
				} else {
					const text = this.l10n._T('scenes.shared.toast.fiscalization.print-document.can-not-change-document-status-error');
					this.fiscalToastService.showWarning(text);
					fiscalRegistryUpdate(text, FiscalDeviceEventStatus.Warning);
				}
			} else {
				if (response.type === SuzInternalStatus.DailyReportRequired) {
					this.fiscalToastService.showDailyReport(this.printDocumentErrorMessage, (response as any).suzMessages || [], async () => {
						await this.printDailyReport(selectedPrinter);
					});
				} else if (response.type === SuzInternalStatus.MappingError) {
					this.fiscalToastService.showMappingError(this.printDocumentErrorMessage, printer.id, (response as any).suzMessages || []);
				} else {
					this.fiscalToastService.showError(this.printDocumentErrorMessage, (response as any).suzMessages || []);
				}
				fiscalRegistryUpdate?.(this.prepareTextForRegistryItem(this.printDocumentErrorMessage, (response as any).suzMessages || []), FiscalDeviceEventStatus.Error);
			}
		} catch(err) {
			this.logger.error(err);
			fiscalRegistryUpdate?.(this.printDocumentErrorMessage, FiscalDeviceEventStatus.Error);
			this.fiscalToastService.showError(this.printDocumentErrorMessage);
		} finally {
			this.fiscalizationEnded({id: entity.id, version: refreshedDocumentVersion});
			this.removeBeforeUnloadEventListener();
		}
	}

	private get printDocumentErrorMessage(): string {
		return this.l10n._T('scenes.shared.toast.fiscalization.print-document.error');
	}

	private prepareTextForRegistryItem(head: string, messages?: string[]): string {
		const data = [head, ...(messages?.length ? messages : [])];
		return data.join(' ');
	}

	private getRepoByKind(kind: SaleDocumentKind): SaleAdvanceRepositoryBase | SaleDocumentRepositoryBase | SaleAdvanceAdjustmentRepositoryBase {
		const mapping: { [key in SaleDocumentKind]?: SaleAdvanceRepositoryBase | SaleDocumentRepositoryBase | SaleAdvanceAdjustmentRepositoryBase } = {
			[SaleDocumentKind.Invoice]: this.saleDocumentRepository,
			[SaleDocumentKind.Receipt]: this.saleDocumentRepository,
			[SaleDocumentKind.VatMarginInvoice]: this.saleDocumentRepository,
			[SaleDocumentKind.AdvanceInvoice]: this.advanceRepository,
			[SaleDocumentKind.AdvanceInvoiceAdjustment]: this.advanceAdjustmentRepository
		};
		const repo = mapping[kind];
		if (!repo) {
			throw new Error(`Repository not found for document kind: ${kind}`);
		}
		return repo;
	}

	private async changeFiscalizationStateOnDocumentAsync(
		documentId: number,
		data: { serialNumber: string, number: string }
	) : Promise<string | undefined> {
		let attemptsAmount = 5;
		let version;

		const setFiscalizeStatus = async () => {
			const fiscalizedDocumentData = {
				value: FiscalizationState.Fiscalized,
				printerNumber: data.serialNumber,
				ticketNumber: data.number
			};

			const newVersion = await this.commandBus.commandAsync(new SaleUpdateFiscalizationStatusCommand({data: fiscalizedDocumentData, id: documentId}));
			return newVersion;
		};
		while (attemptsAmount-- && !version) {
			version = await setFiscalizeStatus();
		}
		return version;
	}

	private async setFiscalRegistryInitEvent(options: FiscalDeviceRegistryCreateEventData): Promise<(description: string, status: FiscalDeviceEventStatus) => void> {
		try {
			const result = await this.commandBus.commandAsync(new FiscalDeviceRegistryCreateEventCommand(options));
			return (description: string, status: FiscalDeviceEventStatus) => {
				try {
					if (!result?.object) return;
					const updatedData: FiscalDeviceRegistryUpdateEventData = {
						...options,
						eventId: result.object.id,
						data: {
							...options.data,
							description,
							status,
							version: result.object.version
						}
					};
					this.commandBus.commandAsync(new FiscalDeviceRegistryUpdateEventCommand(updatedData));
				} catch(err) {
					this.logger.error(err);
				}
			};
		} catch(err) {
			this.logger.error(err);
			return () => {};
		}
	}

	private fiscalizationEnded(params: {id: number, version?: string}): void {
		this.eventBus.emit('document-fiscalization::end', params);
	}

	private fiscalizationStarted(id: number): void {
		this.eventBus.emit('document-fiscalization::start', id);
	}

	private beforeUnloadHandler = (e: BeforeUnloadEvent) => {
		this.insights.trackEvent('FE_WINDOW_EXIT_DURING_FISCAL');
		e.preventDefault();
		e.returnValue = true;
	};

	private addBeforeUnloadEventListener(): void {
		window.addEventListener('beforeunload', this.beforeUnloadHandler);
	}

	private removeBeforeUnloadEventListener(): void {
		window.removeEventListener('beforeunload', this.beforeUnloadHandler);
	}
}
